import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, money } from "../lib/api";
import { PageHeader, Card, Modal, Field, inputClass, EmptyState, PrimaryButton } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Storefront, Plus, Trash, PencilSimple, LinkSimple, CheckCircle, ArrowsLeftRight } from "@phosphor-icons/react";

const STATUS = [
  { value: "activo", label: "Activo", cls: "bg-emerald-50 text-emerald-700" },
  { value: "en_revision", label: "En revisión", cls: "bg-amber-50 text-amber-700" },
  { value: "inactivo", label: "Inactivo", cls: "bg-slate-100 text-slate-600" },
];
const statusMeta = (v) => STATUS.find((s) => s.value === v) || STATUS[0];
const empty = { name: "", company: "", rfc: "", category: "", phone: "", email: "", status: "activo", recurring: false, amount_monthly: 0, charge_day: "", notify_days_before: 3, notes: "" };

export default function Suppliers() {
  const { user } = useAuth();
  const canDelete = ["admin", "gerente_general"].includes(user?.role) || !!user?.can_delete;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const toContractor = async (s) => {
    if (!window.confirm(`¿Enviar "${s.name}" a Contratistas? Se moverá su expediente.`)) return;
    try { await api.post(`/suppliers/${s.id}/convert-to-contractor`); toast.success("Enviado a Contratistas"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const load = () => api.get("/suppliers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const nuevo = searchParams.get("nuevo");
    if (nuevo) {
      setForm({ ...empty, name: nuevo });
      setEditId(null);
      setOpen(true);
      searchParams.delete("nuevo");
      setSearchParams(searchParams, { replace: true });
      toast.info("Completa los datos del proveedor y guarda.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => {
    const days = Array.isArray(s.charge_days) && s.charge_days.length ? s.charge_days.join(", ") : (s.charge_day ? String(s.charge_day) : "");
    setForm({ ...empty, ...s, charge_days: days }); setEditId(s.id); setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const daysArr = String(form.charge_days || "").split(/[,\s]+/).map((x) => parseInt(x, 10)).filter((n) => !isNaN(n) && n >= 1 && n <= 31);
    const uniqDays = [...new Set(daysArr)].sort((a, b) => a - b);
    const payload = { ...form, amount_monthly: parseFloat(form.amount_monthly || 0), charge_days: uniqDays, charge_day: uniqDays.length ? uniqDays[0] : null, notify_days_before: form.notify_days_before === "" ? 3 : parseInt(form.notify_days_before, 10) };
    try {
      if (editId) await api.put(`/suppliers/${editId}`, payload);
      else await api.post("/suppliers", payload);
      toast.success("Proveedor guardado"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { if (!window.confirm("¿Eliminar proveedor?")) return; await api.delete(`/suppliers/${id}`); toast.success("Eliminado"); load(); };
  const linkExpense = async (s) => {
    try { await api.post(`/suppliers/${s.id}/link-expense`); toast.success("Proveedor ligado a Gasto Fijo mensual"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader overline="Directorio" title="Proveedores"
        action={<PrimaryButton onClick={openNew} data-testid="add-supplier-btn"><Plus size={18} weight="bold" /> Nuevo</PrimaryButton>} />

      <div className="flex gap-2 mb-6" data-testid="directory-tabs">
        <button onClick={() => navigate("/contratistas")} data-testid="tab-contratistas" className="px-4 py-1.5 rounded-sm text-sm font-semibold border border-input text-muted-foreground hover:bg-muted transition-colors">Contratistas</button>
        <span className="px-4 py-1.5 rounded-sm text-sm font-semibold bg-primary text-white">Proveedores</span>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Storefront} title="Sin proveedores" subtitle="Registra a tus proveedores con su expediente y estatus. Si son recurrentes, lígalos a un gasto fijo mensual."
          action={<PrimaryButton onClick={openNew}><Plus size={18} weight="bold" /> Agregar proveedor</PrimaryButton>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => {
            const sm = statusMeta(s.status);
            return (
              <Card key={s.id} data-testid={`supplier-card-${s.id}`} className="p-6 hover:-translate-y-0.5 hover:shadow-md transition-transform duration-200">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-lg truncate">{s.name}</div>
                    <div className="text-sm text-muted-foreground truncate">{s.company || s.category || "—"}</div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-sm ${sm.cls}`} data-testid={`supplier-status-${s.id}`}>{sm.label}</span>
                      {s.recurring && <span className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-blue-50 text-blue-700">Recurrente</span>}
                      {s.fixed_expense_id && <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700"><CheckCircle size={12} weight="fill" /> Gasto fijo</span>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(s)} className="p-1.5 text-muted-foreground hover:text-primary"><PencilSimple size={18} /></button>
                    {canDelete && <button onClick={() => remove(s.id)} className="p-1.5 text-muted-foreground hover:text-danger"><Trash size={18} /></button>}
                  </div>
                </div>
                <div className="mt-4 space-y-1 text-sm">
                  {s.category && <div className="text-muted-foreground">Giro: {s.category}</div>}
                  {s.rfc && <div className="text-muted-foreground">RFC: {s.rfc}</div>}
                  {s.phone && <div className="text-muted-foreground">Tel: {s.phone}</div>}
                  {s.email && <div className="text-muted-foreground">{s.email}</div>}
                  {s.amount_monthly > 0 && <div className="font-semibold text-foreground">Mensual: {money(s.amount_monthly)}{(Array.isArray(s.charge_days) && s.charge_days.length) ? ` · cobra día ${s.charge_days.join(", ")}` : (s.charge_day ? ` · cobra día ${s.charge_day}` : "")}</div>}
                </div>
                {s.recurring && !s.fixed_expense_id && (
                  <button onClick={() => linkExpense(s)} data-testid={`supplier-link-${s.id}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
                    <LinkSimple size={15} weight="bold" /> Ligar a Gasto Fijo mensual
                  </button>
                )}
                <div className="mt-3">
                  <button onClick={() => toContractor(s)} data-testid={`to-contractor-${s.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"><ArrowsLeftRight size={14} weight="bold" /> Enviar a Contratistas</button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Editar proveedor" : "Nuevo proveedor"}>
        <form onSubmit={save} className="space-y-4">
          <Field label="Nombre *"><input data-testid="supplier-name" required className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Empresa"><input className={inputClass} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
            <Field label="Giro / categoría"><input className={inputClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ej. Acero, Renta de maquinaria" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="RFC"><input className={inputClass} value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value })} /></Field>
            <Field label="Estatus">
              <select data-testid="supplier-status-select" className={inputClass} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Teléfono"><input className={inputClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Correo"><input className={inputClass} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" data-testid="supplier-recurring" checked={!!form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} />
            Proveedor recurrente (gasto mensual)
          </label>
          {form.recurring && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Monto mensual (MXN)"><input type="number" step="0.01" data-testid="supplier-amount" className={inputClass} value={form.amount_monthly} onChange={(e) => setForm({ ...form, amount_monthly: e.target.value })} /></Field>
              <Field label="Días de cobro (ej. 15, 30)"><input data-testid="supplier-charge-day" className={inputClass} value={form.charge_days ?? ""} onChange={(e) => setForm({ ...form, charge_days: e.target.value })} placeholder="Ej. 15, 30" /></Field>
              <Field label="Avisar (días antes)"><input type="number" min="0" max="30" data-testid="supplier-notify-days" className={inputClass} value={form.notify_days_before} onChange={(e) => setForm({ ...form, notify_days_before: e.target.value })} placeholder="3" /></Field>
            </div>
          )}
          <Field label="Notas"><textarea className={inputClass + " h-20 py-2"} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <PrimaryButton type="submit" disabled={busy} data-testid="save-supplier-btn">{busy ? "Guardando…" : "Guardar"}</PrimaryButton>
        </form>
      </Modal>
    </div>
  );
}
